import { type FC, useState } from "react";

import type { DashboardSourceControlRepositoryUpsertRequest } from "../../dashboard-client/types.gen";
import type { SourceControlRepository } from "../../types";

import { CountBadge } from "../common/CountBadge";
import { EmptyState } from "../common/EmptyState";
import { EyebrowLabel } from "../common/EyebrowLabel";
import { PanelSection } from "../common/PanelSection";
import { SourceControlRepositoryForm } from "./SourceControlRepositoryForm";
import SourceControlRepositoryItem from "./SourceControlRepositoryItem";

interface Props {
  providerId: string;
  repositories: SourceControlRepository[];
  onUpsert: (
    providerId: string,
    input: DashboardSourceControlRepositoryUpsertRequest,
  ) => Promise<void>;
  onDelete: (providerId: string, repositoryId: string) => Promise<void>;
}

const SourceControlRepositoryList: FC<Props> = ({
  providerId,
  repositories,
  onUpsert,
  onDelete,
}) => {
  const [expandedRepoId, setExpandedRepoId] = useState<string | null>(null);

  return (
    <PanelSection
      headerContent={
        <>
          <div>
            <EyebrowLabel>Repositories</EyebrowLabel>
            <p className="font-mono text-[11px] font-bold uppercase tracking-widest text-slate-900 dark:text-white">
              Repository Allowlist
            </p>
          </div>
          <CountBadge>{repositories.length}</CountBadge>
        </>
      }
    >
      {repositories.length === 0 ? (
        <EmptyState
          title="No repositories configured"
          description="Add repositories below or discover them from the provider."
          className="py-6"
        />
      ) : (
        <div className="space-y-3">
          {repositories.map((repo) => (
            <SourceControlRepositoryItem
              key={repo.repository_id}
              repository={repo}
              isExpanded={expandedRepoId === repo.repository_id}
              onToggle={() =>
                setExpandedRepoId(
                  expandedRepoId === repo.repository_id
                    ? null
                    : repo.repository_id,
                )
              }
              onDelete={() => void onDelete(providerId, repo.repository_id)}
            />
          ))}
        </div>
      )}

      <SourceControlRepositoryForm
        providerId={providerId}
        onUpsert={onUpsert}
      />
    </PanelSection>
  );
};

export default SourceControlRepositoryList;
